import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Logo } from '../../features/brand/Primitives';
import { UserContext } from '../../UserContext';
import { useAuthModal } from '../../features/auth/AuthModalContext';
import { useI18n } from '../../features/i18n';
import ErrorBoundary from './ErrorBoundary';
import dataService from '../../services/dataService';
import { SkeletonStyles } from './Skeleton';
import '../../features/brand/cwm-tokens.css';

/**
 * The one branded shell every unified /unified/* route renders inside.
 *
 * Design-system anchor:
 *  - bubble-blue background (`--cwm-bg-bubble`) with a subtle mascot
 *    watermark that matches the archived Landing / Pricing hero bands
 *  - Nunito display type from @fontsource/nunito
 *  - CWM tokens (`cwm-tokens.css`) — radii, shadows, group palette
 *  - white rounded top bar with the stacked logo on the left, primary
 *    links in the middle, role-aware CTA on the right
 *
 * Routes own their own content pane below the bar. When `hero` is set
 * we let the page draw full-bleed behind the bar (Landing-style). When
 * not set, the bar sits on its own white band so content screens (dashboard,
 * checkout, etc.) feel framed.
 */
// Backdrop defaults to `true` so every unified route inherits the same
// bubble-blue + floating-mascot treatment automatically. Landing passes
// `hero` which suppresses it (the gradient hero owns the whole viewport).
// Any screen that explicitly doesn't want it can pass backdrop={false}.
export default function AppShell({ children, hero = false, backdrop = true, active, dense = false, footer = true }) {
  const { userId, setUserId } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { openAuth } = useAuthModal();
  const { t } = useI18n();

  const onLogout = async () => {
    // Supabase-first logout. Clearing the whole localStorage is too
    // aggressive (wipes cart, prefs, etc.). Just drop the userId
    // mapping and let Supabase revoke the session.
    try {
      const { supabase } = await import('../../supabaseClient');
      await supabase.auth.signOut();
    } catch (err) {
      // Non-fatal — still clear local state below.
    }
    localStorage.removeItem('userId');
    if (setUserId) setUserId(null);
    navigate('/unified');
  };

  const currentActive = active || inferActiveFromPath(location.pathname);
  const isLoggedIn = Boolean(userId);

  return (
    <div
      className="cwm-unified-shell"
      style={{
        minHeight: '100vh',
        background: 'var(--cwm-bg-bubble, #E8F3FF)',
        fontFamily: 'var(--cwm-font, "Nunito", system-ui, sans-serif)',
        color: 'var(--cwm-ink, #0A2540)',
        display: 'flex',
        flexDirection: 'column',
        // `position: relative` is load-bearing for the MascotBackdrop.
        // Without it, the absolutely-positioned backdrop resolves against
        // the viewport and only covers the first screen. With it, the
        // backdrop sizes to the full scrollable content height, so the
        // mascots show through every section as you scroll.
        position: 'relative',
      }}
    >
      <SkeletonStyles />
      {(hero || backdrop) && <MascotBackdrop />}

      <BrandNav
        active={currentActive}
        isLoggedIn={isLoggedIn}
        onLogout={onLogout}
        // The unified AuthModal now uses Supabase under the hood
        // (rewired 2026-04-23). Safe to route Sign in / Sign up to
        // the modal again so users stay in the unified brand without
        // being dumped onto the legacy MUI login screen.
        onSignIn={() => openAuth('signin')}
        onSignUp={() => openAuth('signup')}
        hero={hero}
        t={t}
      />

      <main
        style={{
          flex: 1,
          position: 'relative',
          padding: dense ? 0 : undefined,
        }}
      >
        {/* Keyed on pathname so navigating past a bad screen clears the
            boundary state instead of pinning the error page forever. */}
        <ErrorBoundary key={location.pathname}>
          {children}
        </ErrorBoundary>
      </main>

      {footer && <BrandFooter t={t} isLoggedIn={isLoggedIn} />}
    </div>
  );
}

// Top nav only holds public-facing marketing links. Rewards / Loyalty lives
// inside the dashboard tabs (alongside Edit Profile, My Allergies, etc.) so
// it doesn't leak into the marketing funnel. Labels are resolved at render
// time via the i18n dict (see `navLinksFor(t)` below).
// Base link set. The `pricing` destination is context-aware: see
// pricingTargetForPath() below.
const NAV_LINK_KEYS = [
  { key: 'discover',        tKey: 'nav.discover',        to: '/unified/discover' },
  { key: 'for-restaurants', tKey: 'nav.for_restaurants', to: '/unified/for-restaurants' },
  { key: 'pricing',         tKey: 'nav.pricing',         to: '/unified/pricing' },
];

// When someone is browsing the For Business surface (/unified/for-
// restaurants, /unified/restaurant-pricing, /unified/owner-dashboard,
// /unified/menu-manager, /unified/add-restaurant), the top-nav "Pricing"
// link should point to the RESTAURANT pricing page — not bounce them
// into the diner $0/$10/$75 tiers. Everywhere else, it stays on the
// diner pricing page.
function pricingTargetForPath(pathname) {
  const p = (pathname || '').toLowerCase();
  const isMerchantZone =
    p.includes('/for-restaurants') ||
    p.includes('/restaurant-pricing') ||
    p.includes('/owner-dashboard') ||
    p.includes('/menu-manager') ||
    p.includes('/add-restaurant');
  return isMerchantZone ? '/unified/restaurant-pricing' : '/unified/pricing';
}

function inferActiveFromPath(pathname) {
  const p = pathname.toLowerCase();
  if (p.includes('discover')) return 'discover';
  if (p.includes('for-restaurants') || p.includes('restaurant-pricing')) return 'for-restaurants';
  if (p.includes('pricing')) return 'pricing';
  if (p === '/unified' || p === '/unified/') return 'home';
  return null;
}

// Unread count for the bell, shared across pages for a minute so each
// navigation doesn't refetch.
let unreadCache = { at: 0, count: 0 };
function useUnreadCount(enabled) {
  const [count, setCount] = useState(unreadCache.count);
  useEffect(() => {
    if (!enabled) { setCount(0); return; }
    if (Date.now() - unreadCache.at < 60000) { setCount(unreadCache.count); return; }
    let cancelled = false;
    dataService.listNotifications()
      .then((items) => {
        unreadCache = { at: Date.now(), count: items.filter((n) => n.unread).length };
        if (!cancelled) setCount(unreadCache.count);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [enabled]);
  return count;
}

function BrandNav({ active, isLoggedIn, onLogout, onSignIn, onSignUp, hero, t }) {
  const unread = useUnreadCount(isLoggedIn);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  // Close the drawer on route change + lock body scroll while open so the
  // page behind doesn't scroll through the overlay.
  const location = useLocation();

  // Context-aware nav: the Pricing link retargets to restaurant pricing
  // when the visitor is already inside the For Business funnel.
  const NAV_LINKS = NAV_LINK_KEYS.map((l) => ({
    ...l,
    label: t(l.tKey),
    to: l.key === 'pricing' ? pricingTargetForPath(location.pathname) : l.to,
  }));
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mobileOpen]);

  return (
    <>
      {/* Media-query-driven visibility so desktop and mobile share one render
          tree — cleaner than UA sniffing or a resize listener, and it stays
          correct when the user resizes the window. */}
      <style>{navResponsiveCss}</style>

      <header
        className="cwm-unified-nav"
        data-hero={hero ? 'true' : 'false'}
        style={{
          position: 'relative',
          zIndex: 2,
          padding: '14px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          // Hero pages get a glassmorphic frosted bar so text stays legible
          // over the gradient hero. A subtle bottom border reinforces the
          // separation between nav and content without competing for
          // attention.
          background: hero
            ? 'linear-gradient(180deg, rgba(10,37,64,0.22), rgba(10,37,64,0.08))'
            : '#fff',
          backdropFilter: hero ? 'saturate(160%) blur(14px)' : undefined,
          WebkitBackdropFilter: hero ? 'saturate(160%) blur(14px)' : undefined,
          borderBottom: hero ? '1px solid rgba(255,255,255,0.18)' : '1px solid transparent',
          boxShadow: hero ? '0 6px 24px rgba(10,37,64,0.08)' : '0 4px 16px rgba(10,37,64,0.04)',
        }}
      >
        {/* LEFT — the clean icon-only brand mark (bowl + speech bubble with
            food icons), sized for dense nav chrome. No wordmark so the nav
            scales cleanly across translated labels + narrow viewports. */}
        <Link to="/unified" aria-label={t('nav.home_aria')}
              className="cwm-nav-logo"
              style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
          <Logo
            variant={hero ? 'mark-white' : 'mark-blue'}
            height={38}
            alt="Chat With Menu"
            style={hero ? { filter: 'drop-shadow(0 2px 10px rgba(15,127,232,0.35))' } : undefined}
          />
        </Link>

        {/* CENTER — primary nav. Hidden on mobile. On hero (gradient bg)
            pages we use solid white + a soft text shadow so labels read
            against any color underneath. */}
        <nav className="cwm-nav-links" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {NAV_LINKS.map((l) => {
            const isActive = active === l.key;
            return (
              <Link
                key={l.key}
                to={l.to}
                style={{
                  padding: '8px 16px',
                  borderRadius: 999,
                  fontSize: 14,
                  fontWeight: 800,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  color: isActive
                    ? 'var(--cwm-blue-deep, #0F7FE8)'
                    : hero ? '#fff' : 'var(--cwm-gray-700, #3C4043)',
                  background: isActive
                    ? '#fff'
                    : hero ? 'rgba(255,255,255,0.08)' : 'transparent',
                  border: isActive
                    ? '1px solid rgba(15,127,232,0.18)'
                    : hero ? '1px solid rgba(255,255,255,0.22)' : '1px solid transparent',
                  textShadow: hero && !isActive ? '0 1px 2px rgba(10,37,64,0.28)' : 'none',
                  boxShadow: isActive ? '0 4px 14px rgba(10,37,64,0.18)' : 'none',
                  transition: 'all 140ms ease',
                }}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        {/* RIGHT — account CTAs on desktop, hamburger on mobile. */}
        <div className="cwm-nav-actions" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {isLoggedIn ? (
            <>
              <Link
                to="/unified/notifications"
                aria-label={t('nav.notifications')}
                title={t('nav.notifications')}
                style={iconBellStyle(hero)}
              >
                <BellIcon size={16} />
                {unread > 0 && (
                  <span aria-hidden style={{ position: 'absolute', top: 6, right: 7, width: 8, height: 8, borderRadius: 999, background: '#FF3B5C', border: '2px solid #fff' }}/>
                )}
              </Link>
              <Link to="/unified/dashboard" style={linkPill({ filled: false, hero })}>
                {t('nav.dashboard')}
              </Link>
              <button type="button" onClick={onLogout}
                      style={{
                        ...linkPill({ filled: false, hero }),
                        border: 'none', cursor: 'pointer',
                        background: 'transparent',
                        // Text-only button — lean on shadow for contrast on hero.
                        textShadow: hero ? '0 1px 2px rgba(10,37,64,0.28)' : 'none',
                      }}>
                {t('nav.sign_out')}
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={onSignIn}
                      style={{ ...linkPill({ filled: false, hero }), cursor: 'pointer', background: 'transparent' }}>
                {t('nav.sign_in')}
              </button>
              <button type="button" onClick={onSignUp}
                      style={{ ...linkPill({ filled: true, hero }), border: 'none', cursor: 'pointer' }}>
                {t('nav.get_started')}
              </button>
            </>
          )}
        </div>

        {/* MOBILE — hamburger trigger. */}
        <button
          type="button"
          className="cwm-nav-hamburger"
          aria-label="Menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
          style={{
            width: 40, height: 40, borderRadius: 12,
            background: hero ? 'rgba(255,255,255,0.18)' : '#fff',
            color: hero ? '#fff' : 'var(--cwm-blue-deep)',
            border: hero ? '1.5px solid rgba(255,255,255,0.5)' : '1.5px solid var(--cwm-blue-light)',
            backdropFilter: hero ? 'blur(6px)' : undefined,
            WebkitBackdropFilter: hero ? 'blur(6px)' : undefined,
            display: 'none', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', flexShrink: 0,
          }}
        >
          {mobileOpen ? <CloseIcon size={16} /> : <HamburgerIcon size={16} />}
        </button>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <MobileDrawer
            t={t}
            active={active}
            isLoggedIn={isLoggedIn}
            onClose={() => setMobileOpen(false)}
            onLogout={() => { setMobileOpen(false); onLogout(); }}
            onSignIn={() => { setMobileOpen(false); onSignIn(); }}
            onSignUp={() => { setMobileOpen(false); onSignUp(); }}
            navigate={navigate}
          />
        )}
      </AnimatePresence>
    </>
  );
}

// --- helpers for the nav --------------------------------------------------

function linkPill({ filled, hero }) {
  if (filled) {
    // On hero we keep the brand-blue gradient (pops against any hero color
    // and matches the primary CTAs diners will click once below the fold).
    return {
      padding: '10px 18px',
      borderRadius: 999,
      fontSize: 13.5,
      fontWeight: 800,
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      background: 'linear-gradient(90deg, #1E9BFF, #0F7FE8)',
      color: '#fff',
      boxShadow: hero
        ? '0 8px 22px rgba(10,37,64,0.32), 0 0 0 1px rgba(255,255,255,0.2) inset'
        : '0 6px 18px rgba(30,155,255,0.35)',
    };
  }
  // Outline pill. On hero we give it a subtle frosted fill + a brighter
  // border so "Sign in" and "Dashboard" aren't hiding against the gradient.
  return {
    padding: '9px 16px',
    borderRadius: 999,
    fontSize: 13.5,
    fontWeight: 800,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    color: hero ? '#fff' : 'var(--cwm-blue-deep, #0F7FE8)',
    border: hero ? '1.5px solid rgba(255,255,255,0.55)' : '1.5px solid var(--cwm-blue-light, #BDE3FF)',
    background: hero ? 'rgba(255,255,255,0.12)' : 'transparent',
    textShadow: hero ? '0 1px 2px rgba(10,37,64,0.28)' : 'none',
    backdropFilter: hero ? 'blur(6px)' : undefined,
    WebkitBackdropFilter: hero ? 'blur(6px)' : undefined,
  };
}

function iconBellStyle(hero) {
  return {
    width: 38, height: 38, borderRadius: 999,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: hero ? 'rgba(255,255,255,0.18)' : '#fff',
    color: hero ? '#fff' : 'var(--cwm-blue-deep, #0F7FE8)',
    border: hero ? '1.5px solid rgba(255,255,255,0.5)' : '1.5px solid var(--cwm-blue-light, #BDE3FF)',
    backdropFilter: hero ? 'blur(6px)' : undefined,
    WebkitBackdropFilter: hero ? 'blur(6px)' : undefined,
    textDecoration: 'none',
    position: 'relative',
    flexShrink: 0,
  };
}

function BellIcon({ size = 16 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 01-3.4 0"/></svg>;
}
function HamburgerIcon({ size = 16 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M3 6h18M3 12h18M3 18h18"/></svg>;
}
function CloseIcon({ size = 16 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M6 6l12 12M6 18L18 6"/></svg>;
}

// Slide-in mobile drawer. Full-height, right-aligned, with a blurred
// backdrop. Taps outside the drawer close it, so does any route change.
function MobileDrawer({ t, active, isLoggedIn, onClose, onLogout, onSignIn, onSignUp }) {
  const location = useLocation();
  // Same context-aware Pricing retarget as the desktop nav so the
  // mobile drawer never routes a restaurant operator into diner pricing.
  const NAV_LINKS = NAV_LINK_KEYS.map((l) => ({
    ...l,
    label: t(l.tKey),
    to: l.key === 'pricing' ? pricingTargetForPath(location.pathname) : l.to,
  }));
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(10,37,64,0.55)',
        backdropFilter: 'blur(6px)',
        display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <motion.aside
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(86vw, 360px)',
          background: '#fff',
          padding: '20px 22px 28px',
          display: 'flex', flexDirection: 'column', gap: 14,
          boxShadow: '-20px 0 48px rgba(10,37,64,0.22)',
          overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <Logo variant="mascot" height={36} alt="Chat With Menu" />
          <button type="button" onClick={onClose} aria-label="Close"
                  style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: 'var(--cwm-gray-50)', color: 'var(--cwm-gray-700)',
                    border: '1.5px solid var(--cwm-gray-100)',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                  }}>
            <CloseIcon size={14} />
          </button>
        </div>

        <nav style={{ display: 'grid', gap: 4 }}>
          {NAV_LINKS.map((l) => {
            const isActive = active === l.key;
            return (
              <Link key={l.key} to={l.to}
                    style={{
                      padding: '12px 14px', borderRadius: 14,
                      fontSize: 15.5, fontWeight: 800,
                      textDecoration: 'none',
                      color: isActive ? 'var(--cwm-blue-deep)' : 'var(--cwm-ink)',
                      background: isActive ? 'var(--cwm-blue-pale)' : 'transparent',
                      border: `1.5px solid ${isActive ? 'var(--cwm-blue-light)' : 'transparent'}`,
                      transition: 'all 140ms ease',
                    }}>
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div style={{ height: 1, background: 'var(--cwm-gray-100)', margin: '4px 0' }}/>

        {isLoggedIn ? (
          <div style={{ display: 'grid', gap: 6 }}>
            <Link to="/unified/dashboard"
                  style={{ padding: '12px 14px', borderRadius: 14, fontSize: 15, fontWeight: 900, textDecoration: 'none',
                           color: '#fff', background: 'linear-gradient(90deg, #1E9BFF, #0F7FE8)',
                           boxShadow: '0 6px 18px rgba(30,155,255,0.32)', textAlign: 'center' }}>
              {t('nav.dashboard')}
            </Link>
            <Link to="/unified/notifications"
                  style={{ padding: '11px 14px', borderRadius: 14, fontSize: 14.5, fontWeight: 800, textDecoration: 'none',
                           color: 'var(--cwm-blue-deep)', background: '#fff', border: '1.5px solid var(--cwm-blue-light)',
                           display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
              <BellIcon size={14} /> {t('nav.notifications')}
            </Link>
            <Link to="/unified/family-management"
                  style={{ padding: '11px 14px', borderRadius: 14, fontSize: 14.5, fontWeight: 800, textDecoration: 'none',
                           color: 'var(--cwm-blue-deep)', background: '#fff', border: '1.5px solid var(--cwm-blue-light)',
                           textAlign: 'center' }}>
              Family
            </Link>
            <Link to="/unified/profile"
                  style={{ padding: '11px 14px', borderRadius: 14, fontSize: 14.5, fontWeight: 800, textDecoration: 'none',
                           color: 'var(--cwm-gray-700)', background: '#fff', border: '1.5px solid var(--cwm-gray-100)',
                           textAlign: 'center' }}>
              {t('footer.settings')}
            </Link>
            <button type="button" onClick={onLogout}
                    style={{ padding: '11px 14px', borderRadius: 14, fontSize: 14.5, fontWeight: 800,
                             color: 'var(--cwm-red-text)', background: 'transparent', border: 'none',
                             cursor: 'pointer', textAlign: 'center' }}>
              {t('nav.sign_out')}
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            <button type="button" onClick={onSignUp}
                    style={{ padding: '12px 14px', borderRadius: 14, fontSize: 15, fontWeight: 900,
                             color: '#fff', background: 'linear-gradient(90deg, #1E9BFF, #0F7FE8)',
                             border: 'none', cursor: 'pointer',
                             boxShadow: '0 6px 18px rgba(30,155,255,0.32)' }}>
              {t('nav.get_started')}
            </button>
            <button type="button" onClick={onSignIn}
                    style={{ padding: '11px 14px', borderRadius: 14, fontSize: 14.5, fontWeight: 800,
                             color: 'var(--cwm-blue-deep)', background: '#fff',
                             border: '1.5px solid var(--cwm-blue-light)', cursor: 'pointer' }}>
              {t('nav.sign_in')}
            </button>
          </div>
        )}

        <div style={{ height: 1, background: 'var(--cwm-gray-100)', margin: '4px 0' }}/>

        {/* Secondary links — same set as the footer but easier to reach on mobile. */}
        <div style={{ display: 'grid', gap: 2 }}>
          {[
            { to: '/unified/how-it-works', label: t('footer.how_it_works') },
            { to: '/unified/about',        label: t('footer.about') },
            { to: '/unified/billing',      label: t('footer.billing') },
            { to: '/unified/terms',        label: t('footer.terms') },
            { to: '/unified/privacy-policy', label: t('footer.privacy') },
          ].map((l) => (
            <Link key={l.to} to={l.to}
                  style={{ padding: '8px 14px', fontSize: 13, fontWeight: 700,
                           color: 'var(--cwm-gray-500)', textDecoration: 'none' }}>
              {l.label}
            </Link>
          ))}
        </div>
      </motion.aside>
    </motion.div>
  );
}

// Responsive CSS for the nav. Below 820px, the inline nav links + account
// CTAs collapse and the hamburger takes over. On hero pages we need the
// logo + wordmark to stay readable against the gradient — the wordmark
// still shows at the smallest widths so the user always knows where they are.
const navResponsiveCss = `
  /* Below 820px, the inline nav links + account pills collapse and the
     hamburger drawer takes over. The brand mark stays visible at every
     width so the user never loses their place. */
  @media (max-width: 820px) {
    .cwm-unified-nav .cwm-nav-links,
    .cwm-unified-nav .cwm-nav-actions {
      display: none !important;
    }
    .cwm-unified-nav .cwm-nav-hamburger {
      display: inline-flex !important;
    }
  }
`;

function BrandFooter({ t, isLoggedIn }) {
  return (
    <footer
      style={{
        position: 'relative',
        zIndex: 2,
        background: '#fff',
        borderTop: '1px solid var(--cwm-gray-100, #F1F5F9)',
        padding: '28px 32px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 18,
        color: 'var(--cwm-gray-500, #6B7A90)',
        fontSize: 13,
        fontWeight: 500,
      }}
    >
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
        <Logo variant="mascot" height={28} alt="" style={{ opacity: 0.75 }} />
        {/* Hardcoded to the company's actual founding year rather than
            `new Date().getFullYear()` — we were showing 2026 in April 2026
            when the business is still a 2025 entity. Bump manually when
            we roll over. */}
        <span>{t('footer.copyright', { year: 2025 })}</span>
      </div>
      {/* Public footer is marketing + legal. Billing and Settings are
          account-only surfaces and don't belong on the logged-out footer
          — they either 404 or flash the redirect before bouncing. */}
      <nav style={{ display: 'inline-flex', alignItems: 'center', gap: 22, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <Link to="/unified/how-it-works" style={footerLink}>{t('footer.how_it_works')}</Link>
        <Link to="/unified/about" style={footerLink}>{t('footer.about')}</Link>
        <Link to="/unified/pricing" style={footerLink}>{t('nav.pricing')}</Link>
        <Link to="/unified/for-restaurants" style={footerLink}>{t('nav.for_restaurants')}</Link>
        {isLoggedIn && <Link to="/unified/billing"  style={footerLink}>{t('footer.billing')}</Link>}
        {isLoggedIn && <Link to="/unified/profile" style={footerLink}>{t('footer.settings')}</Link>}
        <Link to="/unified/terms" style={footerLink}>{t('footer.terms')}</Link>
        <Link to="/unified/privacy-policy" style={footerLink}>{t('footer.privacy')}</Link>
        <a href="mailto:hello@chatwithmenu.com" style={footerLink}>{t('footer.contact')}</a>
      </nav>
    </footer>
  );
}

const footerLink = {
  color: 'var(--cwm-gray-700, #3C4043)',
  textDecoration: 'none',
  fontWeight: 700,
  fontSize: 13,
};

// Page-wide mascot watermark. Sporadic — truly random x/y, wide variance
// in size + opacity + rotation — so the eye reads them as a floating
// organic constellation rather than a grid of evenly-spaced tiles.
// Deliberately avoids any stratification / band logic (that was what
// made the previous pass look like a pattern instead of floating).
function MascotBackdrop() {
  const dotsRef = useRef(null);
  if (!dotsRef.current) {
    const arr = [];
    // 10 mascots across the full scrollable height. Fewer than before
    // so each one reads as a deliberate accent, not wallpaper.
    const COUNT = 10;
    for (let i = 0; i < COUNT; i++) {
      // Weighted-random size — most are small (22–40px), a couple get
      // bigger (40–66px). Mimics depth: small feels "far back," big
      // feels "foreground blur." Natural-looking without being random
      // soup.
      const sizeRoll = Math.random();
      const size = sizeRoll < 0.75
        ? 22 + Math.random() * 18   // 22–40 (small, common)
        : 40 + Math.random() * 26;  // 40–66 (larger, rarer)
      arr.push({
        top:  Math.random() * 100,
        left: Math.random() * 100,
        size,
        // Opacity tracks size loosely — bigger ones fade more so they
        // don't dominate. 0.03–0.12 overall.
        op: 0.03 + Math.random() * 0.09,
        rot: -30 + Math.random() * 60,
      });
    }
    dotsRef.current = arr;
  }
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        overflow: 'hidden',
      }}
    >
      {dotsRef.current.map((d, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            top: `${d.top}%`,
            left: `${d.left}%`,
            opacity: d.op,
            transform: `translate(-50%, -50%) rotate(${d.rot}deg)`,
          }}
        >
          <Logo variant="mascot-white" height={d.size} alt="" />
        </div>
      ))}
    </div>
  );
}
